import { Outlet, Link, useLocation } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { useHasAnyTournamentRole, useIsAdmin, useSession } from "../../lib/me";
import { ChevronRightIcon, EmptyState, LoadingBlock, SignInCard } from "../../components/ui";

/**
 * Admin console gate: signed in AND holding some console access — the ADMIN
 * allowlist (spec §3, §6), or a role granted on one specific tournament.
 * Reads the same session query the nav does, so asking costs nothing here —
 * and the answer is a stated role rather than an inference from a 403.
 *
 * A scoped grant opens the door and almost nothing behind it. Which pages that
 * is, is not decided here: the per-tournament pages derive their own capability
 * table, and the organization-wide ones sit behind `RequireGlobalAdmin` below.
 * Gating every route here instead would mean this component knowing the whole
 * table, which is the coupling those two arrangements exist to avoid.
 */
export function AdminLayout() {
  const { idToken, ready } = useAuth();
  const { pathname } = useLocation();
  const session = useSession();
  const hasTournamentRole = useHasAnyTournamentRole();

  if (!ready) return <LoadingBlock />;
  if (!idToken) {
    return (
      <SignInCard
        title="Admin console"
        hint="Sign in with an organizer account to continue."
        next="/admin"
      />
    );
  }
  if (session.isPending) return <LoadingBlock label="Checking access…" />;
  if (!session.data?.isAdmin && !hasTournamentRole) {
    return (
      <EmptyState
        title="Your account is not an admin"
        hint="Ask an organizer to add you to the admin allowlist, or to give you a role on one of their tournaments."
        className="mx-auto max-w-md"
      />
    );
  }

  return (
    <div className="space-y-6">
      {pathname !== "/admin" ? (
        <nav className="animate-fade-in">
          <Link
            to="/admin"
            className="group inline-flex items-center gap-1 text-sm font-medium text-slate-500 transition-colors hover:text-brandText"
          >
            <ChevronRightIcon className="h-4 w-4 rotate-180 transition-transform duration-150 group-hover:-translate-x-0.5" />
            All tournaments
          </Link>
        </nav>
      ) : null}
      <Outlet />
    </div>
  );
}

/**
 * The half of the console that belongs to the organization rather than to one
 * tournament: the venue library, branding, the About page, the allowlist, and
 * the Users page. Every write behind these is global-admin-only in the API's
 * capability table, so a scoped caller reaching one of them by bookmark, by
 * history, or by a link that outlived their access would get a page that looks
 * fully operational and 403s on Save.
 *
 * A route guard rather than a check inside each page, because the property is
 * about the whole group and a page added to that group later should inherit it
 * instead of having to remember it. `/admin/venues` is in the group even though
 * the API lets a tournamentAdmin *read* venues — that read is for the event
 * form's picker, and this page is the editor.
 *
 * No pending branch: this renders inside AdminLayout's `Outlet`, which already
 * waited on the same session query, so the answer is warm by the time this
 * mounts.
 */
export function RequireGlobalAdmin() {
  // Bound before it is branched on, not called inside the `if`: every hook on
  // this page has to run on every render, and a hook in a condition is how that
  // stops being obvious to the next reader.
  const isAdmin = useIsAdmin();

  if (!isAdmin)
    return (
      <EmptyState
        title="That page is for organization admins"
        hint="Your access is to individual tournaments, not to the whole organization. Everything you can reach is on the tournament list."
        className="mx-auto max-w-md"
      />
    );

  return <Outlet />;
}
